import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toastr from 'toastr';
import { FaTimes, FaSave, FaCalculator, FaQuestionCircle, FaCloud } from 'react-icons/fa';

const ApiQuestionsModal = ({ question, isOpen, onClose, onSave }) => {
  const { t } = useTranslation();
  const [apiQuestions, setApiQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [calculatedResult, setCalculatedResult] = useState(null);

  useEffect(() => {
    if (isOpen && question) {
      // Use API questions from question prop if available, otherwise fetch them
      if (question.apiQuestions && question.apiQuestions.length > 0) {
        setApiQuestions(question.apiQuestions);
        
        // Initialize answers with existing user answers
        const existingAnswers = {};
        question.apiQuestions.forEach(apiQ => {
          if (apiQ.userAnswer) {
            existingAnswers[apiQ.id] = apiQ.userAnswer;
          }
        });
        setAnswers(existingAnswers);
        setCalculatedResult(null);
      } else if (question.id) {
        fetchApiQuestions();
      }
    }
  }, [isOpen, question]);

  const fetchApiQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api-questions/questions/${question.id}/api-questions`);
      if (response.data.status) {
        setApiQuestions(response.data.apiQuestions || []);
        
        // Initialize answers with existing user answers
        const existingAnswers = {};
        (response.data.apiQuestions || []).forEach(apiQ => {
          if (apiQ.userAnswer) {
            existingAnswers[apiQ.id] = apiQ.userAnswer;
          }
        });
        setAnswers(existingAnswers);
      } else {
        toastr.error(response.data.message || t('apiQuestions.failedToFetch'));
      }
    } catch (error) {
      console.error('Error fetching API questions:', error);
      toastr.error(t('apiQuestions.failedToFetch'));
    } finally {
      setLoading(false);
    }
  };

  const detectInputType = (apiQuestionText, unit) => {
    const text = apiQuestionText.toLowerCase();
    if (unit === 'file_upload') return 'file';
    if (unit === 'date_range') return 'date';
    if (text.includes('date') || text.includes('period')) return 'date';
    if (text.includes('upload') || text.includes('bill')) return 'file';
    if (text.includes('percentage') || text.includes('%')) return 'number';
    if (text.includes('pesos') || text.includes('clp') || text.includes('spend')) return 'number';
    if (text.includes('select') || text.includes('choose')) return 'select';
    return 'text';
  };

  const getPlaceholder = (apiQuestionText, unit) => {
    if (unit) {
      if (unit.includes('/')) {
        return `${t('apiQuestions.enterValueIn')} ${unit}`;
      }
      return `${t('apiQuestions.enterUnit')} ${unit}`;
    }
    return t('apiQuestions.enterYourAnswer');
  };

  const handleAnswerChange = (apiQuestionId, value) => {
    setAnswers(prev => ({ ...prev, [apiQuestionId]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const savePromises = Object.entries(answers)
        .filter(([_, answer]) => answer !== '' && answer !== null && answer !== undefined)
        .map(([apiQuestionId, answer]) =>
          axios.post('/api-questions/submit-api-question-answer', {
            apiQuestionId: parseInt(apiQuestionId),
            questionId: question.id,
            userQuestionsAssignmentId: question.userQuestionsAssignment,
            answer: answer
          })
        );

      if (savePromises.length === 0) {
        toastr.warning(t('apiQuestions.answerAtLeastOne'));
        return;
      }

      await Promise.all(savePromises);
      toastr.success(t('apiQuestions.answersSavedSuccessfully'));
      
      // Call onSave to refresh the parent component
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving API question answers:', error);
      toastr.error(t('apiQuestions.failedToSaveAnswers'));
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    // First save all answers
    await handleSave();
    
    setCalculating(true);
    try {
      const response = await axios.post('/api-questions/trigger-api-call', {
        questionId: question.id,
        userQuestionsAssignmentId: question.userQuestionsAssignment
      });

      if (response.data.status) {
        setCalculatedResult(response.data.result);
        toastr.success(t('apiQuestions.calculationComplete', { answer: response.data.answerText }));
        
        // Refresh the parent component to show the calculated answer
        if (onSave) {
          onSave();
        }
        
        // Close modal after a short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        toastr.error(response.data.message || t('apiQuestions.failedToCalculate'));
      }
    } catch (error) {
      console.error('Error calculating via API:', error);
      toastr.error(error.response?.data?.message || t('apiQuestions.failedToCalculateViaApi'));
    } finally {
      setCalculating(false);
    }
  };

  if (!isOpen) return null;

  const allQuestionsAnswered = apiQuestions.every(apiQ => 
    answers[apiQ.id] && answers[apiQ.id] !== '' && answers[apiQ.id] !== null
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-green-600 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <FaCalculator className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white text-xl font-semibold">{t('apiQuestions.title')}</h3>
                <p className="text-blue-100 text-sm">
                  {t('apiQuestions.subtitle')}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="bg-transparent text-white hover:text-gray-200 transition-colors duration-200"
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {loading && apiQuestions.length === 0 ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
              <p className="text-gray-500">{t('apiQuestions.loading')}</p>
            </div>
          ) : apiQuestions.length > 0 ? (
            <>
              {calculatedResult && (
                <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <FaCloud className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-green-800">{t('apiQuestions.calculationResult')}</h4>
                  </div>
                  <p className="text-green-700">
                    <strong>CO₂e:</strong> {calculatedResult.co2e_tons?.toFixed(2)} {calculatedResult.co2e_unit || 'tCO₂e'}
                  </p>
                  {calculatedResult.emission_factor_name && (
                    <p className="text-sm text-green-600 mt-1">
                      {t('apiQuestions.using')}: {calculatedResult.emission_factor_name}
                    </p>
                  )}
                </div>
              )}

              {apiQuestions.map((apiQuestion, index) => {
                const inputType = detectInputType(apiQuestion.apiQuestionText, apiQuestion.unit);
                const placeholder = getPlaceholder(apiQuestion.apiQuestionText, apiQuestion.unit);
                
                return (
                  <div key={apiQuestion.id} className="mb-6">
                    <label className="block text-lg font-semibold text-gray-700 mb-3">
                      {index + 1}. {apiQuestion.apiQuestionText}
                      {apiQuestion.unit && apiQuestion.unit !== 'file_upload' && (
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          ({apiQuestion.unit})
                        </span>
                      )}
                    </label>
                    
                    {inputType === 'file' ? (
                      <div className="border-2 border-gray-200 border-dashed rounded-lg p-4 text-center hover:border-green-400 transition-colors">
                        <input
                          type="file"
                          className="hidden"
                          id={`file-${apiQuestion.id}`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              // For now, just store the file name. In production, you'd upload the file first
                              handleAnswerChange(apiQuestion.id, file.name);
                            }
                          }}
                        />
                        <label
                          htmlFor={`file-${apiQuestion.id}`}
                          className="cursor-pointer text-green-600 hover:text-green-700"
                        >
                          {t('apiQuestions.clickToUploadFile')}
                        </label>
                        {answers[apiQuestion.id] && (
                          <p className="mt-2 text-sm text-gray-600">{answers[apiQuestion.id]}</p>
                        )}
                      </div>
                    ) : inputType === 'date' ? (
                      <input
                        type="date"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-200"
                        value={answers[apiQuestion.id] || ''}
                        onChange={(e) => handleAnswerChange(apiQuestion.id, e.target.value)}
                      />
                    ) : (
                      <input
                        type={inputType === 'number' ? 'number' : 'text'}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-200 text-gray-900"
                        placeholder={placeholder}
                        value={answers[apiQuestion.id] || ''}
                        onChange={(e) => handleAnswerChange(apiQuestion.id, e.target.value)}
                      />
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaQuestionCircle className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg">{t('apiQuestions.noApiQuestionsAvailable')}</p>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200"
          >
{t('common.cancel')}
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={loading || apiQuestions.length === 0}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaSave className="w-4 h-4" />
              <span>{loading ? t('common.saving') : t('apiQuestions.saveAnswers')}</span>
            </button>
            
            {apiQuestions.length > 0 && (
              <button
                onClick={handleCalculate}
                disabled={calculating || !allQuestionsAnswered}
                className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaCalculator className="w-4 h-4" />
                <span>{calculating ? t('apiQuestions.calculating') : t('apiQuestions.calculateViaApi')}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiQuestionsModal;

